import { useState, useEffect, useCallback } from 'react';

function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

function useElementPositionAndWindowSize(elementRef) {
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const updatePositionAndSize = useCallback(() => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setPosition((prev) => {
        if (
          prev.top !== rect.top ||
          prev.left !== rect.left ||
          prev.width !== rect.width ||
          prev.height !== rect.height
        ) {
          return {
            top: rect.top > 0 ? rect.top : prev.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          };
        }
        return prev;
      });
    }
    setWindowSize((prev) => {
      if (
        prev.width !== window.innerWidth ||
        prev.height !== window.innerHeight
      ) {
        return {
          width: window.innerWidth,
          height: window.innerHeight,
        };
      }
      return prev;
    });
  }, [elementRef]);

  const debouncedUpdate = useCallback(debounce(updatePositionAndSize, 100), [
    updatePositionAndSize,
  ]);

  useEffect(() => {
    let rafId;
    const initialUpdate = () => {
      rafId = requestAnimationFrame(() => {
        updatePositionAndSize();
      });
    };

    const timeoutId = setTimeout(initialUpdate, 1200);

    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('scroll', debouncedUpdate);

    const observer = new ResizeObserver(debouncedUpdate);
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('scroll', debouncedUpdate);
      observer.disconnect();
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [elementRef, debouncedUpdate]);

  return { position, windowSize };
}

export default useElementPositionAndWindowSize;